import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import YouTempLogo from "@/components/ui/YouTempLogo";
import YouTempLogoIcon from "@/components/ui/YouTempLogoIcon";
import { featuresByRole } from "@/components/layout/DesktopMegaMenu";
import { PrefetchLink } from "@/components/ui/prefetch-link";
import { prefetchRoute } from "@/lib/prefetch";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { usePersona } from "@/contexts/PersonaContext";
import { appHref } from "@/lib/appLinks";
import { PERSONA_ACCENT_CLASSES, PERSONA_ICONS, type PersonaAccentKey, audienceToAccent } from "@/lib/themeConstants";
import type { AudienceType } from "@/components/home/PersonaSelector";
import { ThemeToggle } from "@/components/layout/ThemeToggle";

const LazyMobileNav = lazy(() =>
  import("@/components/layout/MobileNav").then(m => ({ default: m.MobileNav }))
);

const LazyDesktopMegaMenu = lazy(() => import("@/components/layout/DesktopMegaMenu"));

const LazyPersonaMegaMenu = lazy(() =>
  import("@/components/layout/PersonaMegaMenu").then(m => ({ default: m.PersonaMegaMenu }))
);

interface HeaderProps {
  hidden?: boolean;
}

export function Header({ hidden = false }: HeaderProps = {}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [personaMenuOpen, setPersonaMenuOpen] = useState(false);
  const location = useLocation();
  const { isPublicFlagsLoading, canShowSignup, canShowPersonaToggle } = usePlatformSettings();
  const { activeAudience, setActiveAudience } = usePersona();

  const personaLogoColor: Record<AudienceType, string> = {
    temps: "hsl(var(--worker))",
    agencies: "hsl(var(--agency))",
    employers: "hsl(var(--employer))",
  };

  const personaIconMap: Record<AudienceType, { icon: typeof PERSONA_ICONS.worker; bg: string; bgActive: string; text: string }> = {
    temps: { icon: PERSONA_ICONS.worker, bg: 'bg-worker/10', bgActive: 'bg-worker/20', text: 'text-worker' },
    agencies: { icon: PERSONA_ICONS.agency, bg: 'bg-agency/10', bgActive: 'bg-agency/20', text: 'text-agency' },
    employers: { icon: PERSONA_ICONS.employer, bg: 'bg-employer/10', bgActive: 'bg-employer/20', text: 'text-employer' },
  };

  const togglePersonaMenu = useCallback(() => setPersonaMenuOpen(prev => !prev), []);

  // Phase 2: removed pre-emptive idle prefetch of MobileNav / DesktopMegaMenu.
  // Both chunks now load on actual user intent (hover for the desktop mega
  // menu via onMouseEnter / the lazy import; tap for mobile nav via the
  // menu button), eliminating a second-cluster long task on the homepage.

  useEffect(() => {
    setExpandedSection(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const allNavItems = [
    { label: "Temps", href: "/temps/", hasFeatures: true, features: featuresByRole["Temps"], roleHref: "/temps/", roleName: "temp", accentKey: "worker" as PersonaAccentKey },
    { label: "Agencies", href: "/agencies/", hasFeatures: true, features: featuresByRole["Agencies"], roleHref: "/agencies/", roleName: "agency", accentKey: "agency" as PersonaAccentKey },
    { label: "Employers", href: "/employers/", hasFeatures: true, features: featuresByRole["Employers"], roleHref: "/employers/", roleName: "employer", accentKey: "employer" as PersonaAccentKey },
    { label: "About Us", href: "/about/" },
    // Pricing intentionally omitted from public header. The /pricing/ route
    // remains accessible (noindex) but should not be promoted in nav until a
    // dedicated `show_public_pricing` platform flag exists. The previous
    // gating coupled this to registration flags, which is the wrong semantic.
  ];

  const navItems = useMemo(() => allNavItems, []);

  const toggleSection = (label: string) => {
    setExpandedSection(expandedSection === label ? null : label);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full bg-background border-b border-border",
        "transition-transform duration-300 will-change-transform",
        hidden && "-translate-y-full"
      )}
    >
      <div className="container flex h-16 items-center justify-between">
        {/* Left: Logo */}
        <Link to="/" className="flex-shrink-0 flex items-center text-secondary dark:text-foreground">
          <YouTempLogo width={124} height={32} sizeClass="h-8 w-auto" primaryColor={activeAudience ? personaLogoColor[activeAudience] : "hsl(var(--primary))"} />
        </Link>

        {/* Center: Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
        {navItems.map((item) => (
          <div
            key={item.label}
            className="relative"
            onMouseEnter={() => {
              if (item.hasFeatures) setActiveDropdown(item.label);
              prefetchRoute(item.href);
            }}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            <PrefetchLink
              to={item.href}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1",
                location.pathname.startsWith(item.href)
                  ? (item.accentKey ? PERSONA_ACCENT_CLASSES[item.accentKey].textAccent : "text-primary")
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {item.label}
              {item.hasFeatures && <ChevronDown className="h-4 w-4" />}
            </PrefetchLink>
            {item.hasFeatures && activeDropdown === item.label && (
              <Suspense fallback={null}>
                <LazyDesktopMegaMenu
                  roleLabel={item.label}
                  roleHref={item.roleHref!}
                  roleName={item.roleName!}
                  accentKey={item.accentKey}
                />
              </Suspense>
            )}
          </div>
        ))}
        </nav>

        {/* Right: Persona + CTAs + Hamburger */}
        <div className="flex items-center gap-2">
          {/*
           * Persona toggle slot — always reserves a 40×40 footprint so the
           * prerendered HTML, the hydrated loading state, and the resolved
           * state all share identical header geometry. The toggle is admin
           * gated; keeping the slot reserved avoids a horizontal shift on
           * either resolution outcome.
           */}
          <div className="relative h-10 w-10 flex items-center justify-center">
            {isPublicFlagsLoading ? (
              <span aria-hidden className="block h-10 w-10" />
            ) : canShowPersonaToggle ? (
              <button
                type="button"
                onClick={togglePersonaMenu}
                className={cn(
                  "flex items-center justify-center h-10 w-10 rounded-lg",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  activeAudience
                    ? cn(
                        personaIconMap[activeAudience].bgActive,
                        personaIconMap[activeAudience].text,
                        personaMenuOpen ? 'ring-2 ring-offset-1' : 'hover:opacity-80'
                      )
                    : cn(
                        "text-primary",
                        personaMenuOpen
                          ? "bg-primary/20 text-primary"
                          : "bg-primary/10 hover:bg-primary/20 hover:text-primary animate-heartbeat"
                      )
                )}
                aria-label="Select your role"
              >
                {activeAudience ? (
                  (() => {
                    const IconComp = personaIconMap[activeAudience].icon;
                    return <IconComp className="h-5 w-5" />;
                  })()
                ) : (
                  <YouTempLogoIcon size={20} primaryColor="hsl(var(--primary))" secondaryColor="currentColor" />
                )}
              </button>
            ) : (
              // Toggle disabled by flag — keep slot reserved to avoid any
              // post-load horizontal shift of the CTAs to the right.
              <span aria-hidden className="block h-10 w-10" />
            )}
            {personaMenuOpen && canShowPersonaToggle && (
              <Suspense fallback={null}>
                <LazyPersonaMegaMenu
                  activeAudience={activeAudience}
                  onSelect={setActiveAudience}
                  onClose={() => setPersonaMenuOpen(false)}
                />
              </Suspense>
            )}
          </div>

          {(() => {
            const accent = PERSONA_ACCENT_CLASSES[audienceToAccent(activeAudience)];
            return (
              <div className="hidden lg:flex items-center gap-3">
                <ThemeToggle />
                <Button variant="outline" asChild className={accent.btnOutline}>
                  <a href={appHref(`/auth?tab=signin`)}>Sign In</a>
                </Button>
                {/*
                 * Sign Up slot — reserved with a stable width so the nav
                 * never shifts when the signup flag resolves. Loading state
                 * renders an invisible placeholder of identical size; if the
                 * flag is disabled the slot stays reserved (collapsing it
                 * would shift the persona slot + nav back to the right).
                 */}
                {isPublicFlagsLoading ? (
                  <span
                    aria-hidden
                    className="inline-block h-10 w-[88px] rounded-md"
                  />
                ) : canShowSignup ? (
                  <Button asChild className={accent.btn}>
                    <a href={appHref(`/auth?tab=signup${activeAudience ? `&role=${activeAudience === 'temps' ? 'temp' : activeAudience === 'agencies' ? 'agency' : 'employer'}` : ''}`)}>Sign Up</a>
                  </Button>
                ) : (
                  <span
                    aria-hidden
                    className="inline-block h-10 w-[88px] rounded-md"
                  />
                )}
              </div>
            );
          })()}

          {/* Mobile menu trigger — permanent 40×40 slot.
           * Lives OUTSIDE the lazy Suspense boundary so the slot never
           * collapses while the MobileNav chunk loads (which would let the
           * persona toggle shift right and then back). Icon morphs in place. */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 shrink-0 rounded-lg hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <div className="relative h-6 w-6">
              <Menu
                className={cn(
                  "h-6 w-6 absolute inset-0 transition-opacity duration-150",
                  mobileOpen ? "opacity-0" : "opacity-100"
                )}
              />
              <X
                className={cn(
                  "h-6 w-6 absolute inset-0 transition-opacity duration-150",
                  mobileOpen ? "opacity-100" : "opacity-0"
                )}
              />
            </div>
          </button>
          {mobileOpen && (
            <Suspense fallback={null}>
              <LazyMobileNav
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
                navItems={navItems}
                expandedSection={expandedSection}
                toggleSection={toggleSection}
                isAnyRegistrationEnabled={canShowSignup}
                activeAudience={activeAudience}
              />
            </Suspense>
          )}
        </div>
      </div>
    </header>
  );
}
